import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { VisitCard } from "../components/VisitCard";
import { Button } from "../components/ui/Button";
import { Card, CardBody } from "../components/ui/Card";
import { api, ApiException } from "../lib/api";

interface VisitListItem {
  id: string;
  title: string | null;
  provider_name_snapshot: string | null;
  provider_specialty_snapshot: string | null;
  facility_name_snapshot: string | null;
  starts_at: string;
  arrive_lead_minutes: number;
  visit_category: string | null;
  status: "open" | "accepted" | "cancelled" | "completed";
  ride_required: number;
  primary_acceptor_id: string | null;
  pending_approval?: number;
}

interface MeResponse {
  user: { id: string } | null;
  circles: Array<{ id: string; role: "admin" | "member" }>;
}

export function VisitsList() {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [visits, setVisits] = useState<VisitListItem[] | null>(null);

  useEffect(() => {
    api
      .get<MeResponse>("/api/me")
      .then((d) => {
        if (!d.user) return navigate("/signin", { replace: true });
        if (d.circles.length === 0) return navigate("/app", { replace: true });
        setMe(d);
        const today = new Date().toISOString();
        return api
          .get<{ visits: VisitListItem[] }>(`/api/circles/${d.circles[0]!.id}/visits?from=${encodeURIComponent(today)}`)
          .then((r) => setVisits(r.visits));
      })
      .catch((err) => {
        if (err instanceof ApiException && err.status === 401) navigate("/signin", { replace: true });
      });
  }, [navigate]);

  const circle = me?.circles[0];
  const isAdmin = circle?.role === "admin";

  return (
    <AppShell activeTab="visits">
      <div className="mx-auto max-w-3xl px-5 py-6 sm:py-8">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Visits</h1>
          <Link to="/app/visits/new">
            <Button>+ {isAdmin ? "New visit" : "Propose visit"}</Button>
          </Link>
        </div>

        <div className="mt-5 space-y-3">
          {visits === null && <div className="text-text-muted">Loading…</div>}
          {visits && visits.length === 0 && (
            <Card>
              <CardBody>
                <h2 className="text-lg font-semibold">No upcoming visits</h2>
                <p className="mt-1 text-text-muted">
                  {isAdmin ? "Post the next appointment so the family can see it." : "If you know about an upcoming appointment, you can propose it — admins approve before it goes out to the family."}
                </p>
                <div className="mt-4">
                  <Link to="/app/visits/new"><Button>{isAdmin ? "Create a visit" : "Propose a visit"}</Button></Link>
                </div>
              </CardBody>
            </Card>
          )}
          {visits?.map((v) => <VisitCard key={v.id} visit={v} />)}
        </div>
      </div>
    </AppShell>
  );
}
